import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import './index.css';
import { tenantConfig, injectTenantStyles, resolveWhitelabelConfig } from './lib/tenant';
import { applyPreset } from './components/ThemePresetSelector';
import type { ThemePreset } from './components/ThemePresetSelector';

// Apply saved theme preset before React mounts (prevents flash)
const savedPreset = (localStorage.getItem('theme-preset') as ThemePreset) || 'custom';
applyPreset(savedPreset);

const queryClient = new QueryClient();

(async () => {
  // Modo 2: busca branding na API pelo hostname (whitelabel dinâmico).
  // Em deploys com VITE_TENANT_ID ou em localhost, retorna imediatamente.
  await resolveWhitelabelConfig();

  // Inicializar customizações por cliente (após resolveWhitelabelConfig para que
  // tenantConfig.id já esteja definido corretamente).
  await import('./custom/init');

  // Invalidar sessão armazenada se o tenant não bater com o atual.
  // Evita que um login do Rheoly seja reaproveitado em um whitelabel (e vice-versa).
  const authRaw = localStorage.getItem('auth-storage');
  if (authRaw) {
    try {
      const { state } = JSON.parse(authRaw);
      if (state?.token) {
        const payload = JSON.parse(atob(state.token.split('.')[1]));
        const storedTenant = payload.tenantId ?? 'default';
        if (storedTenant !== tenantConfig.id) {
          localStorage.removeItem('auth-storage');
        }
      }
    } catch { /* token malformado — ignora */ }
  }

  document.title = tenantConfig.appName;
  injectTenantStyles();

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>
  );
})();
